import classNames from 'classnames'
import { FC } from 'react'
import { SentimentDonut } from 'src/components/SentimentDonut'
import {
  DeepDive,
  DeepDiveBrandRow,
} from 'src/services/aiListeningApi/brandProfile.types'
import styles from './deepDive.module.scss'
import { DashMeter } from '../shared/charts/DashMeter'
import { BRAND_LABEL_TONES, Pill } from '../shared/Pill'
import {
  BRAND_LABELS,
  firstLetter,
  formatPercent,
  formatPosition,
} from '../shared/utils'

const LEADER_COLOR = '#25262d'
const YOU_COLOR = '#8080ff'
const OTHER_COLORS = ['#9ca3af', '#c7ced1', '#dde1e4', '#eceef0']

// Brand rows show "Tracked" for the target too: it is tracked by definition.
export const brandLabelPill = (row: DeepDiveBrandRow) => {
  const label = row.label === 'you' ? 'tracked' : row.label
  return (
    <Pill dot outline tone={BRAND_LABEL_TONES[label]}>
      {BRAND_LABELS[label]}
    </Pill>
  )
}

interface CompetitivePositionProps {
  position: DeepDive['competitivePosition']
  totalModels: number
}

export const CompetitivePosition: FC<CompetitivePositionProps> = ({
  position,
  totalModels,
}) => {
  const leader = position.brands[0]
  const you = position.brands.find((brand) => brand.label === 'you')
  const youName = you?.name ?? 'Your brand'
  let otherIndex = 0
  const colored = position.brands.map((brand) => {
    if (brand === leader && brand !== you) return { brand, color: LEADER_COLOR }
    if (brand === you) return { brand, color: YOU_COLOR }
    const color = OTHER_COLORS[Math.min(otherIndex, OTHER_COLORS.length - 1)]
    otherIndex += 1
    return { brand, color }
  })
  const isLeading = position.gapToLeader <= 0

  return (
    <section className={styles.card}>
      <div>
        <h2 className={styles.cardTitle}>Competitive AI Position</h2>
        <p className={styles.cardSubtitle}>
          How {youName} compares with competitors across AI visibility and
          positioning
        </p>
      </div>

      <div className={styles.positionGrid}>
        <div className={styles.shareColumn}>
          <span className={styles.blockTitle}>Recommendation Share</span>
          <div className={styles.donut}>
            <SentimentDonut
              bands={colored
                .filter(({ brand }) => brand.recommendationShare > 0)
                .map(({ brand, color }) => ({
                  color,
                  value: brand.recommendationShare,
                }))}
              size={190}
              strokeWidth={22}
            />
            <span className={styles.donutCenter}>
              <strong>{formatPercent(position.yourShare)}</strong>
              <span>Your share</span>
            </span>
          </div>
          <div className={styles.donutLegend}>
            {colored.slice(0, 4).map(({ brand, color }) => (
              <span className={styles.donutLegendItem} key={brand.key}>
                <span className={styles.dot} style={{ background: color }} />
                {brand.name}{' '}
                <strong>{formatPercent(brand.recommendationShare)}</strong>
              </span>
            ))}
          </div>
          {isLeading ? (
            <Pill dot size="md" tone="green">
              {youName} leads this prompt
            </Pill>
          ) : (
            <Pill dot size="md" tone="amber">
              {Math.round(position.gapToLeader)} pts behind{' '}
              {position.leaderName}
            </Pill>
          )}
        </div>

        <div className={styles.brandTable}>
          <div className={classNames(styles.brandGrid, styles.brandHead)}>
            <span />
            <span>Brand</span>
            <span>Appeared</span>
            <span>#1 Recs</span>
            <span className={styles.alignRight}>Avg. Pos</span>
          </div>
          {colored.map(({ brand, color }, index) => {
            const isYou = brand.label === 'you'
            return (
              <div
                className={classNames(styles.brandGrid, styles.brandRow, {
                  [styles.brandRowYou]: isYou,
                })}
                key={brand.key}
              >
                <span className={styles.muted}>{index + 1}</span>
                <span className={styles.brandName}>
                  <span
                    className={styles.brandAvatar}
                    style={{
                      background: color,
                      color:
                        color === LEADER_COLOR || isYou ? '#fff' : '#25262d',
                    }}
                  >
                    {firstLetter(brand.name)}
                  </span>
                  <span className={styles.ellipsis}>{brand.name}</span>
                  {brandLabelPill(brand)}
                </span>
                <span className={styles.dashCell}>
                  <DashMeter
                    color={color}
                    count={brand.appeared}
                    of={totalModels}
                  />
                  {brand.appeared}/{totalModels}
                </span>
                <span
                  className={classNames(styles.dashCell, {
                    [styles.bad]: isYou && brand.primaries === 0,
                  })}
                >
                  <DashMeter
                    color={color}
                    count={brand.primaries}
                    of={totalModels}
                  />
                  {brand.primaries}/{totalModels}
                </span>
                <span
                  className={classNames(styles.position, styles.alignRight, {
                    [styles.accent]: isYou,
                  })}
                >
                  {formatPosition(brand.avgPosition)}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {(position.leaderWhy.length > 0 || position.youCompete.length > 0) && (
        <div className={styles.whyGrid}>
          <div className={styles.whyBox}>
            <div className={styles.whyHead}>
              <span className={styles.whyTitle}>
                Why {position.leaderName} Wins
              </span>
              <span className={styles.whyBadge}>
                Leader · {formatPercent(leader?.recommendationShare)}
              </span>
            </div>
            {position.leaderWhy.map((item) => (
              <div className={styles.whyRow} key={item.attribute}>
                <span>{item.attribute}</span>
                <DashMeter
                  className={styles.whyMeter}
                  color={LEADER_COLOR}
                  count={item.count}
                  of={item.of}
                />
                <span className={styles.whyCount}>
                  {item.count}/{item.of}
                </span>
              </div>
            ))}
          </div>
          {!isLeading && (
            <div className={classNames(styles.whyBox, styles.whyBoxYou)}>
              <div className={styles.whyHead}>
                <span className={classNames(styles.whyTitle, styles.accent)}>
                  How {youName} Competes
                </span>
                <span className={classNames(styles.whyBadge, styles.accent)}>
                  You · {formatPercent(position.yourShare)}
                </span>
              </div>
              {position.youCompete.length === 0 && (
                <span className={styles.muted}>
                  No attribute is tied to {youName} in this run.
                </span>
              )}
              {position.youCompete.map((item) => (
                <div className={styles.whyRow} key={item.attribute}>
                  <span>{item.attribute}</span>
                  <DashMeter
                    className={styles.whyMeter}
                    color={YOU_COLOR}
                    count={item.count}
                    of={item.of}
                  />
                  <span className={styles.whyCount}>
                    {item.count}/{item.of}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
